import { ENV } from '@/core/utils/env.utils';
import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { API_ENDPOINTS } from './endpoints';
import { storageService } from '@/shared/utils/storage.service';
import { logger } from '@/core/services/logger.service';

type UnknownRecord = Record<string, unknown>;

function baseUrlFor(isMock: boolean): string {
  return isMock ? ENV.MOCK_API_BASE_URL : ENV.API_BASE_URL;
}

function pickString(source: unknown, ...keys: string[]): string | undefined {
  if (!source || typeof source !== 'object') return undefined;

  const record = source as UnknownRecord;
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) return value;
  }

  return undefined;
}

declare module 'axios' {
  export interface AxiosRequestConfig {
    isMock?: boolean;
  }
}

// Create the base API client factory
const createApiClient = ({ isMock: defaultIsMock = false } = {}) => {
  const client = axios.create({
    baseURL: baseUrlFor(defaultIsMock),
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  });

  // Request interceptor - Handle per-endpoint mocking and add auth token
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // Per-endpoint mocking override
      if (config.isMock !== undefined) {
        config.baseURL = baseUrlFor(config.isMock);
      }

      const token = storageService.getItem<string>('accessToken');

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error: AxiosError) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor - Handle errors and token refresh
  client.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      // Handle 401 Unauthorized with token refresh
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          const refreshToken = storageService.getItem<string>('refreshToken');

          if (!refreshToken) {
            throw new Error('No refresh token available');
          }

          const useMock = originalRequest.isMock === true;
          const refreshBase = baseUrlFor(useMock).replace(
            /\/$/,
            ''
          );
          const refreshPath = API_ENDPOINTS.AUTH.REFRESH_TOKEN;

          const refreshResponse = await axios.post(
            `${refreshBase}${refreshPath}`,
            { refresh_token: refreshToken },
            {
              headers: {
                'Content-Type': 'application/json',
              },
            }
          );

          const d = refreshResponse.data?.data;
          const tokens =
            d && typeof d === 'object' && 'tokens' in (d as UnknownRecord)
              ? (d as UnknownRecord).tokens
              : undefined;
          const newAccessToken =
            pickString(d, 'access_token', 'accessToken') ??
            pickString(tokens, 'access_token', 'accessToken');
          const newRefreshToken =
            pickString(d, 'refresh_token', 'refreshToken') ??
            pickString(tokens, 'refresh_token', 'refreshToken');

          if (!newAccessToken) {
            throw new Error('Invalid refresh response');
          }

          storageService.setItem('accessToken', newAccessToken);
          if (newRefreshToken) {
            storageService.setItem('refreshToken', newRefreshToken);
          }

          // Update the original request with new token
          originalRequest.headers =
            originalRequest.headers ?? axios.AxiosHeaders.from({});
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

          // Retry the original request
          return client(originalRequest);
        } catch (refreshError) {
          logger.error('Token refresh failed', refreshError, 'API');

          // Clear tokens and redirect to login
          storageService.removeItem('accessToken');
          storageService.removeItem('refreshToken');

          // Redirect to login - you might want to use your router here
          if (typeof window !== 'undefined') {
            window.location.href = '/auth/login';
          }

          return Promise.reject(new Error('Authentication failed'));
        }
      }

      // Handle other HTTP errors
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;

        logger.error(`API Error ${status}`, data, 'API');

        // You can add more specific error handling here
        switch (status) {
          case 403:
            logger.warn('Forbidden: Insufficient permissions', null, 'API');
            break;
          case 404:
            logger.warn('Not found', null, 'API');
            break;
          case 500:
            logger.error('Internal server error', null, 'API');
            break;
          default:
            logger.info(`HTTP status ${status}`, null, 'API');
        }
      } else if (error.request) {
        // Network error
        logger.error('Network error - no response received', null, 'API');
      } else {
        logger.error('Request setup error', error.message, 'API');
      }

      return Promise.reject(error);
    }
  );

  return client;
};

// Create default instance
const apiClient = createApiClient();

// Export both the factory and default instance
export { createApiClient };
export default apiClient;